import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type { Task } from 'ember-concurrency'
import { task, timeout, didCancel } from 'ember-concurrency'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type { IntlService } from 'ember-intl'
import type Store from '@ember-data/store'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type WindowService from 'teacher-ui-client/services/window'
import type SessionService from 'teacher-ui-client/services/session'
import { LayoutBlocksConfirmModal } from 'teacher-ui-client/components/layout-blocks/confirm-modal'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { t } from 'ember-intl'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { LinkTo } from '@ember/routing'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { not } from 'ember-truth-helpers'
import type SubscriptionCoordinator from 'teacher-ui-client/models/subscription-coordinator'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

const mfaStatusUrl = join(config.readingEggsBaseUrl, 'mfa/status').href

type UpdateSettingsTask = Task<void, [teacher: Teacher]>

interface Signature {
  Element: HTMLDivElement
  Args: {
    subscriptionType: SubscriptionType
    usesExternalAuthentication?: boolean
    isRolloverInProgress?: boolean
  }
}

export class AccountSettings extends Component<Signature> {
  @service window!: WindowService

  @service intl!: IntlService

  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  @service session!: SessionService

  get currentSubco() {
    return this.subscriptionType.currentSubco
  }

  get currentTeacher() {
    return this.session.currentTeacher
  }

  get isCurrentSubco() {
    return this.currentTeacher.id === this.currentSubco?.id
  }

  get isOneRoster() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  get receiveStudentReports() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return this.currentTeacher.receiveReadingStudentReports
      case SubscriptionType.Maths:
        return this.currentTeacher.receiveMathsStudentReports
      // TODO does this do anything?
      case SubscriptionType.Writing:
        return this.currentTeacher.receiveWritingStudentReports
      default:
        return undefined
    }
  }

  @tracked showRemoveSubcoModal = false

  @tracked showCreateSubcoModal = false

  saveTeacher = task({ restartable: true }, async (teacher: Teacher) => {
    await timeout(500)
    await teacher.save()
  })

  removeSubco = task({ drop: true }, async (teacher: Teacher) => {
    const subcoRecord = teacher.subscriptionCoordinators.find((subco) =>
      subco.isForSubscriptionType(this.args.subscriptionType),
    )
    await subcoRecord?.destroyRecord()
  })

  createSubco = task({ drop: true }, async (teacher: Teacher) => {
    const subcoRecord = this.store.createRecord<SubscriptionCoordinator>('subscription-coordinator', {})
    subcoRecord.school = teacher.school
    subcoRecord.teacher = teacher
    subcoRecord.product = this.args.subscriptionType
    await subcoRecord.save()
  })

  @action
  toggleReceiveStudentReports() {
    const { currentTeacher } = this
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        currentTeacher.receiveReadingStudentReports = !currentTeacher.receiveReadingStudentReports
        break
      case SubscriptionType.Maths:
        currentTeacher.receiveMathsStudentReports = !currentTeacher.receiveMathsStudentReports
        break
      case SubscriptionType.Writing:
        // TODO does this do anything?
        currentTeacher.receiveWritingStudentReports = !currentTeacher.receiveWritingStudentReports
        break
      default:
        break
    }

    void this.performSettingsUpdate(this.saveTeacher, currentTeacher)
  }

  @action
  removeSubcoModal(value: boolean) {
    this.showRemoveSubcoModal = value
  }

  @action
  async removeSubcoConfirm() {
    await this.performSettingsUpdate(this.removeSubco, this.currentTeacher)
    this.removeSubcoModal(false)
    this.redirectToLogin()
  }

  @action
  createSubcoModal(value: boolean) {
    this.showCreateSubcoModal = value
  }

  @action
  async createSubcoConfirm() {
    await this.performSettingsUpdate(this.createSubco, this.currentTeacher)
    this.createSubcoModal(false)
    this.redirectToLogin()
  }

  async performSettingsUpdate(updateTask: UpdateSettingsTask, teacher: Teacher) {
    try {
      await updateTask.perform(teacher)

      const msg = this.intl.t('pages.myAccount.settingsFlashSuccess', { name: teacher.fullName })
      this.flashQueue.addSuccess({ subtitle: msg })
    } catch (e: any) {
      if (didCancel(e)) return
      if (isConflictError(e)) return this.resourceConflict.handleError('update teacher settings', e)

      const msg = this.intl.t('pages.myAccount.settingsFlashFail', { name: teacher.fullName })
      this.flashQueue.addFail({ subtitle: msg })
    }
  }

  /**
   * After a users' role changes, need to reauth them or otherwise certain requests will return unauthorized. They can
   * be re-authed by redirecting them to /login, which should then send them back home.
   */
  redirectToLogin = () => {
    this.window.getWindow.location.assign(config.loginUrl)
  }

  <template>
    <div class="space-y-8" ...attributes>
      <div class="flex flex-col items-start space-y-4">
        <div class="font-semibold">{{t "pages.myAccount.emailSettings"}}</div>
        <label class="inline-flex cursor-pointer items-center gap-2 font-normal select-none">
          <input
            data-test-receive-student-reports
            type="checkbox"
            class="cursor-pointer"
            name="receive-student-reports-{{@subscriptionType}}"
            checked={{this.receiveStudentReports}}
            {{on "input" this.toggleReceiveStudentReports}}
          />
          {{t "pages.myAccount.receiveStudentReports.label"}}
          <PhosphorIcon @icon="phosphor-info-fill" /><Tooltip
            @text={{t "pages.myAccount.receiveStudentReports.tooltip"}}
          />
        </label>
      </div>
      <div class="flex flex-col items-start space-y-4">
        {{#unless @usesExternalAuthentication}}
          <div class="font-semibold">{{t "pages.myAccount.securitySettings"}}</div>
          <UiButtonLink data-test-2fa-link href={{mfaStatusUrl}} target="_blank" rel="noopener noreferrer">
            <span class="flex items-center gap-2">
              {{t "pages.myAccount.2FaLink.label"}}
              <PhosphorIcon @icon="phosphor-info-fill" />
              <Tooltip @text={{t "pages.myAccount.2FaLink.tooltip"}} />
            </span>
          </UiButtonLink>
        {{/unless}}
        {{#unless this.isOneRoster}}
          {{#if this.isCurrentSubco}}
            <span>{{t "pages.myAccount.removeSubco.buttonPre" subscriptionTypeFormatted=(t @subscriptionType)}}</span>
            <span>
              <UiButton
                data-test-remove-subco
                @appearance={{ButtonAppearance.red}}
                {{on "click" (fn this.removeSubcoModal true)}}
                disabled={{@isRolloverInProgress}}
              >
                {{t "pages.myAccount.removeSubco.button"}}
              </UiButton>
              {{#if @isRolloverInProgress}}
                <Tooltip @text={{t "pages.myAccount.subcoLocked"}} />
              {{/if}}
            </span>
          {{else if (not this.currentSubco)}}
            <p>{{t "pages.myAccount.createSubco.buttonPre" subscriptionTypeFormatted=(t @subscriptionType)}}</p>
            <span>
              <UiButton
                data-test-create-subco
                @appearance={{ButtonAppearance.regular}}
                {{on "click" (fn this.createSubcoModal true)}}
                disabled={{@isRolloverInProgress}}
              >
                {{t "pages.myAccount.createSubco.button"}}
              </UiButton>
              {{#if @isRolloverInProgress}}
                <Tooltip @text={{t "pages.myAccount.subcoLocked"}} />
              {{/if}}
            </span>
          {{/if}}
        {{/unless}}
      </div>
    </div>
    <LayoutBlocksConfirmModal
      @title={{t "pages.myAccount.removeSubco.modalHeader"}}
      @showModal={{this.showRemoveSubcoModal}}
      @cancel={{fn this.removeSubcoModal false}}
      @confirm={{this.removeSubcoConfirm}}
      @cancelButtonText={{t "cancel"}}
      @confirmButtonText={{t "pages.myAccount.removeSubco.modalConfirm"}}
      @confirmButtonAppearance={{ButtonAppearance.red}}
      @isSubmitting={{this.removeSubco.isRunning}}
    >
      <p class="mb-3">{{t
          "pages.myAccount.removeSubco.modalWarning"
          subscriptionTypeFormatted=(t @subscriptionType)
        }}</p>
      <p class="mb-3 font-semibold">{{t "pages.myAccount.removeSubco.modalQuestion"}}</p>
    </LayoutBlocksConfirmModal>
    <LayoutBlocksConfirmModal
      @title={{t "pages.myAccount.createSubco.modalHeader"}}
      @showModal={{this.showCreateSubcoModal}}
      @cancel={{fn this.createSubcoModal false}}
      @confirm={{this.createSubcoConfirm}}
      @cancelButtonText={{t "cancel"}}
      @confirmButtonText={{t "pages.myAccount.createSubco.modalConfirm"}}
      @isSubmitting={{this.createSubco.isRunning}}
    >
      <p class="mb-3">{{t "pages.myAccount.createSubco.modalDescription1"}}</p>
      <p class="mb-3">{{t
          "pages.myAccount.createSubco.modalDescription2"
          subscriptionTypeFormatted=(t @subscriptionType)
        }}</p>
      <p class="mb-3">
        {{t "pages.myAccount.createSubco.modalDescription3.pre"}}
        <LinkTo @route="subscription-type.contact-us">{{t
            "pages.myAccount.createSubco.modalDescription3.link"
          }}</LinkTo>
      </p>
    </LayoutBlocksConfirmModal>
  </template>
}

export default AccountSettings
